class HeaderDrawerSteps extends HTMLElement {
  get details() {
    return this.querySelector(':scope > details');
  }

  get drawer() {
    return this.querySelector('.menu-drawer-steps');
  }

  get toggleButton() {
    return this.details?.querySelector(':scope > summary');
  }

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.addEventListener('toggle', this.onToggle, true);
    document.addEventListener('keyup', this.onKeyUp);
    document.addEventListener('focusin', this.onSearchFocus);
    window.addEventListener('resize', this.setTop);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('toggle', this.onToggle, true);
    document.removeEventListener('keyup', this.onKeyUp);
    document.removeEventListener('focusin', this.onSearchFocus);
    window.removeEventListener('resize', this.setTop);
    document.documentElement.removeAttribute('scroll-lock');
  }

  get isOpen() {
    return Boolean(this.details?.classList.contains('menu-open'));
  }

  onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const toggle = target.closest('[data-steps-toggle]');
    if (toggle && toggle === this.toggleButton) {
      event.preventDefault();
      this.isOpen ? this.close() : this.open();
      return;
    }

    const submenuToggle = target.closest('[data-steps-submenu]');
    if (submenuToggle) {
      event.preventDefault();
      this.openSubmenu(submenuToggle.parentElement);
      return;
    }

    const back = target.closest('[data-steps-back]');
    if (back) {
      event.preventDefault();
      this.closeSubmenu(back.closest('details'));
      return;
    }

    if (target.closest('[data-steps-close]')) {
      this.close();
      return;
    }

    if (target.closest('[data-steps-wishlist]')) {
      this.close();
      setTimeout(() => {
        const wishlist = document.querySelector('#vitals-wishlist-header .Vtl-WishlistHeader');
        if (wishlist instanceof HTMLElement) wishlist.click();
      }, 300);
    }
  };

  onToggle = (event) => {
    if (event.target === this.details && this.details.open) this.setTop();
  };

  onSearchFocus = (event) => {
    if (!this.isOpen || !(event.target instanceof Element)) return;
    if (event.target.closest('predictive-search-component, .search-modal, [role="search"]')) this.close();
  };

  onKeyUp = (event) => {
    if (event.key === 'Escape' && this.isOpen) this.close();
  };

  setTop = () => {
    const header = document.getElementById('header-component');
    const bottom = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
    this.style.setProperty('--steps-drawer-top', `${bottom}px`);
  };

  open() {
    clearTimeout(this.closeTimer);
    const header = document.getElementById('header-component');
    if (header?.dataset.scrollDirection === 'down') header.dataset.scrollDirection = 'up';
    this.setTop();
    this.details.open = true;
    this.toggleButton?.setAttribute('aria-expanded', 'true');
    document.documentElement.setAttribute('scroll-lock', '');

    requestAnimationFrame(() => {
      this.setTop();
      this.details.classList.add('menu-open');
    });
  }

  close() {
    this.details.classList.remove('menu-open');
    this.drawer.classList.remove('menu-drawer--has-submenu-opened');
    this.toggleButton?.setAttribute('aria-expanded', 'false');
    document.documentElement.removeAttribute('scroll-lock');

    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => {
      if (this.isOpen) return;
      this.details.open = false;
      this.drawer.querySelectorAll('details').forEach((submenu) => {
        submenu.classList.remove('menu-open');
        submenu.open = false;
      });
    }, 350);
  }

  openSubmenu(submenu) {
    if (!(submenu instanceof HTMLDetailsElement)) return;
    clearTimeout(submenu.closeTimer);
    submenu.open = true;

    requestAnimationFrame(() => {
      submenu.classList.add('menu-open');
      this.drawer.classList.add('menu-drawer--has-submenu-opened');
    });
  }

  closeSubmenu(submenu) {
    if (!(submenu instanceof HTMLDetailsElement)) return;
    submenu.classList.remove('menu-open');
    this.drawer.classList.remove('menu-drawer--has-submenu-opened');

    clearTimeout(submenu.closeTimer);
    submenu.closeTimer = setTimeout(() => {
      if (!submenu.classList.contains('menu-open')) submenu.open = false;
    }, 350);
  }
}

if (!customElements.get('header-drawer-steps')) {
  customElements.define('header-drawer-steps', HeaderDrawerSteps);
}
